import React, { useMemo, useState } from 'react';
import {
  Card,
  Descriptions,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Alert,
  Spin,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import {
  useIRNByDeliveryNote,
  useCreateIRNForDeliveryNote,
  useApproveIRN,
  useRejectIRN,
} from '../../hooks/useQCWorkflow';
import { useUsers } from '@/hooks/useUsers';
import { buildUserIdToLabelMap, resolveUserDisplayFromMap } from '@/utils/userDisplay';

const { TextArea } = Input;

interface InspectionReleaseNoteProps {
  deliveryNoteId: string;
}

const InspectionReleaseNoteComponent: React.FC<InspectionReleaseNoteProps> = ({ deliveryNoteId }) => {
  const [form] = Form.useForm();
  const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);

  const { data: irn, isLoading, refetch } = useIRNByDeliveryNote(deliveryNoteId);
  const { data: users = [] } = useUsers({ is_active: true });
  const userDisplayMap = useMemo(() => buildUserIdToLabelMap(users), [users]);
  const ru = (id?: string | null) => resolveUserDisplayFromMap(userDisplayMap, id, '—');
  const createMutation = useCreateIRNForDeliveryNote();
  const approveMutation = useApproveIRN();
  const rejectMutation = useRejectIRN();

  const handleCreate = async () => {
    try {
      await createMutation.mutateAsync(deliveryNoteId);
      refetch();
    } catch (error) {
      console.error('Error creating IRN:', error);
    }
  };

  const handleApprove = async () => {
    if (!irn) return;
    try {
      await approveMutation.mutateAsync({ id: irn.id });
      refetch();
    } catch (error) {
      console.error('Error approving IRN:', error);
    }
  };

  const handleReject = async (values: { rejectionReason: string }) => {
    if (!irn) return;
    try {
      await rejectMutation.mutateAsync({
        id: irn.id,
        rejectionReason: values.rejectionReason,
      });
      setIsRejectModalVisible(false);
      form.resetFields();
      refetch();
    } catch (error) {
      console.error('Error rejecting IRN:', error);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Draft: 'default',
      'Pending Approval': 'processing',
      Approved: 'success',
      Rejected: 'error',
      Cancelled: 'default',
    };
    return colors[status] || 'default';
  };

  if (isLoading) {
    return <Spin />;
  }

  if (!irn) {
    return (
      <Card>
        <Alert
          message="No Inspection Release Note"
          description="Create an IRN for this delivery note. Quality must approve it before the shipment can be marked in transit (when this note is linked to a work order)."
          type="info"
          showIcon
          action={
            <Space>
              <Button type="primary" onClick={handleCreate} loading={createMutation.isPending}>
                Create IRN
              </Button>
            </Space>
          }
        />
      </Card>
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Card
        title={
          <Space>
            <span>Inspection Release Note</span>
            <Tag color={getStatusColor(irn.status)}>{irn.status}</Tag>
          </Space>
        }
        extra={
          <Space>
            {irn.status === 'Pending Approval' && (
              <>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={handleApprove}
                  loading={approveMutation.isPending}
                >
                  Approve
                </Button>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => setIsRejectModalVisible(true)}
                >
                  Reject
                </Button>
              </>
            )}
          </Space>
        }
      >
        <Descriptions column={2} bordered>
          <Descriptions.Item label="IRN Number">{irn.irn_number}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={getStatusColor(irn.status)}>{irn.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Release Quantity">
            {irn.release_quantity != null && !Number.isNaN(Number(irn.release_quantity))
              ? Number(irn.release_quantity).toFixed(2)
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Release Date">
            {irn.release_date ? new Date(irn.release_date).toLocaleDateString() : '-'}
          </Descriptions.Item>
          {irn.released_by && (
            <Descriptions.Item label="Released By">
              {irn.releaser?.name || ru(irn.released_by)}
            </Descriptions.Item>
          )}
          {irn.approved_date && (
            <>
              <Descriptions.Item label="Approved Date">
                {new Date(irn.approved_date).toLocaleDateString()}
              </Descriptions.Item>
              <Descriptions.Item label="Approved By">
                {irn.approver?.name || ru(irn.approved_by)}
              </Descriptions.Item>
            </>
          )}
          {irn.rejection_reason && (
            <Descriptions.Item label="Rejection Reason" span={2}>
              {irn.rejection_reason}
            </Descriptions.Item>
          )}
          {irn.notes && (
            <Descriptions.Item label="Notes" span={2}>
              {irn.notes}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {irn.status === 'Approved' && (
        <Alert
          message="IRN Approved"
          description="This delivery note may proceed to in transit when other requirements are met."
          type="success"
          showIcon
        />
      )}

      {irn.status === 'Rejected' && (
        <Alert
          message="IRN Rejected"
          description="Resolve quality concerns and create or resubmit an IRN before shipping."
          type="error"
          showIcon
        />
      )}

      <Modal
        title="Reject IRN"
        open={isRejectModalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setIsRejectModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={rejectMutation.isPending}
      >
        <Form form={form} layout="vertical" onFinish={handleReject}>
          <Form.Item
            name="rejectionReason"
            label="Rejection Reason"
            rules={[{ required: true, message: 'Please enter rejection reason' }]}
          >
            <TextArea rows={4} placeholder="Enter reason for rejection" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default InspectionReleaseNoteComponent;
